import React, { useState } from 'react';
import { X, Smartphone, Mail, Shield, QrCode, Copy, Check } from 'lucide-react';

const MfaSetupModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('method'); // method, setup, verify, complete
  const [mfaMethod, setMfaMethod] = useState('authenticator');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [secretKey, setSecretKey] = useState('JBSWY3DPEHPK3PXP');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState('');

  if (!isOpen) return null;

  const handleMethodSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    // Simulate API call to setup MFA method
    setTimeout(() => {
      if (mfaMethod === 'authenticator') {
        // Generate backup codes
        const codes = Array.from({ length: 8 }, () => 
          Math.random()?.toString(36)?.substring(2, 8)?.toUpperCase()
        );
        setBackupCodes(codes);
      }
      setLoading(false);
      setStep('setup');
    }, 1000);
  };

  const handleVerificationSubmit = async (e) => {
    e?.preventDefault();
    if (verificationCode !== '123456') {
      setError('Invalid verification code. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    // Simulate API call to verify and enable MFA
    setTimeout(() => {
      setLoading(false);
      setStep('complete');
      setSuccess('Multi-factor authentication has been successfully enabled!');
    }, 1000);
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator?.clipboard?.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const resetModal = () => {
    setStep('method');
    setMfaMethod('authenticator');
    setPhoneNumber('');
    setVerificationCode('');
    setError('');
    setSuccess('');
    setCopied('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Multi-Factor Authentication Setup</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Choose MFA Method */}
          {step === 'method' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Shield className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <p className="text-gray-600">Choose your preferred multi-factor authentication method</p>
              </div>

              <form onSubmit={handleMethodSubmit} className="space-y-4">
                {/* MFA Method Selection */}
                <div className="space-y-3">
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      mfaMethod === 'authenticator' ?'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setMfaMethod('authenticator')}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="mfa"
                        value="authenticator"
                        checked={mfaMethod === 'authenticator'}
                        onChange={(e) => setMfaMethod(e?.target?.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <Smartphone className="h-5 w-5 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900">Authenticator App</div>
                        <div className="text-sm text-gray-500">Use Google Authenticator or similar app</div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      mfaMethod === 'sms' ?'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setMfaMethod('sms')}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="mfa"
                        value="sms"
                        checked={mfaMethod === 'sms'}
                        onChange={(e) => setMfaMethod(e?.target?.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <Mail className="h-5 w-5 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900">SMS Verification</div>
                        <div className="text-sm text-gray-500">Receive codes via text message</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone Number Input for SMS */}
                {mfaMethod === 'sms' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e?.target?.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <X className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700">{error}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || (mfaMethod === 'sms' && !phoneNumber)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Setting up...
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Setup Instructions */}
          {step === 'setup' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <QrCode className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {mfaMethod === 'authenticator' ? 'Scan QR Code' : 'Verify Phone Number'}
                </h3>
              </div>

              {mfaMethod === 'authenticator' ? (
                <div className="space-y-4">
                  {/* QR Code Placeholder */}
                  <div className="flex justify-center">
                    <div className="w-48 h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <QrCode className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">QR Code for Authenticator App</p>
                      </div>
                    </div>
                  </div>

                  {/* Manual Entry */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 mb-2">Can't scan? Enter manually:</div>
                      <div className="flex items-center justify-between bg-white p-2 rounded border">
                        <code className="text-xs font-mono">{secretKey}</code>
                        <button
                          onClick={() => copyToClipboard(secretKey, 'secret')}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          {copied === 'secret' ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Backup Codes */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="font-medium text-yellow-800 mb-2">Backup Codes</div>
                    <p className="text-sm text-yellow-700 mb-3">
                      Save these backup codes in a safe place. You can use them to access your account if you lose your device.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {backupCodes?.map((code, index) => (
                        <div key={index} className="bg-white p-2 rounded text-center font-mono text-sm">
                          {code}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => copyToClipboard(backupCodes?.join('\n'), 'backup')}
                      className="w-full flex items-center justify-center space-x-2 text-sm text-yellow-700 hover:text-yellow-800"
                    >
                      {copied === 'backup' ? (
                        <>
                          <Check className="h-4 w-4" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          <span>Copy All Codes</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Instructions */}
                  <div className="text-sm text-gray-600 space-y-2">
                    <p><strong>Instructions:</strong></p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Install Google Authenticator or a similar app</li>
                      <li>Scan the QR code or enter the secret key manually</li>
                      <li>Save your backup codes in a secure location</li>
                      <li>Enter the 6-digit code from your app below</li>
                    </ol>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    We've sent a verification code to {phoneNumber}
                  </p>
                  <p className="text-sm text-gray-500">
                    Demo code: 123456
                  </p>
                </div>
              )}

              <form onSubmit={handleVerificationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="000000"
                    maxLength="6"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <X className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700">{error}</span>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setStep('method')}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || verificationCode?.length !== 6}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Verifying...
                      </>
                    ) : (
                      'Verify & Enable'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: Completion */}
          {step === 'complete' && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">MFA Successfully Enabled!</h3>
                <p className="text-gray-600">
                  Your account is now protected with multi-factor authentication.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-700">
                  <div className="font-medium mb-1">What happens next?</div>
                  <ul className="text-left space-y-1">
                    <li>• You'll need to provide a code from your {mfaMethod} when signing in</li>
                    <li>• Keep your backup codes in a safe place</li>
                    <li>• You can disable MFA anytime from your security settings</li>
                  </ul>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MfaSetupModal;